import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Plus, Headset, ShieldCheck, MonitorSmartphone, FileCheck } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { CTABand } from "../../components/site/CTABand";
import { ContactAside } from "../../components/EnquiryForm";
import { ServiceRequestForm } from "../../components/ServiceRequestForm";
import { Reveal, Stagger, StaggerItem } from "../../components/motion/Reveal";

const ACTIONS = [
  { icon: Headset, title: "Raise a Service Request", text: "Something not working? Tell us what and where — our support team will schedule remote or on-site help.", cta: "Raise a request", to: "#support-form" },
  { icon: ShieldCheck, title: "AMC Support", text: "Annual maintenance with scheduled preventive visits, priority breakdown response and health reports.", cta: "About AMC", to: "/services/amc" },
  { icon: MonitorSmartphone, title: "Remote Support", text: "Most configuration and software issues resolved remotely — quickly and securely.", cta: "How it works", to: "/services/remote-support" },
  { icon: FileCheck, title: "Warranty & Documentation", text: "Warranty registered at procurement, as-built documentation at handover — we keep your records straight.", cta: "Our delivery process", to: "/services/commissioning" },
];

const FAQS = [
  { q: "Which areas do you cover?", a: "We are based in Ernakulam and serve all fourteen districts of Kerala — Kasaragod to Thiruvananthapuram. On-site assessments, installations and support visits are available statewide." },
  { q: "Do you provide AMC for systems you didn't install?", a: "Yes. We begin with an audit of your existing infrastructure, document its condition, and then offer an AMC tailored to what is actually on your site." },
  { q: "How do I raise a service request?", a: "Use the form below, call us, or email support@nexsecure.in with your site location and a short description. AMC clients receive priority scheduling." },
  { q: "What are your support hours?", a: "Our office is open Monday to Saturday, 9:30 AM – 5:30 PM IST. Contractual support hours for AMC and managed-services clients are defined per agreement." },
  { q: "Can you take over an unfinished or troubled project?", a: "Yes — this is common. We start with a site survey and documentation review, stabilise what exists, and then complete or remediate the work to a documented standard." },
  { q: "Do you supply equipment only, without installation?", a: "We recommend design-led supply with installation and commissioning, but we can discuss supply-only arrangements where your team handles deployment." },
];

const Faq = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10" data-testid={`faq-${i}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-6 py-6 text-left group" data-testid={`faq-toggle-${i}`}>
        <span className="font-marcellus text-lg sm:text-xl text-white group-hover:text-nx-goldlight transition-colors">{q}</span>
        <Plus size={18} className={`text-nx-goldlight shrink-0 transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
            <p className="pb-6 text-sm sm:text-base text-slate-400 leading-relaxed max-w-2xl" data-testid={`faq-answer-${i}`}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Support() {
  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="support-page">
      <SiteHeader />
      <PageHero
        crumb="Support"
        eyebrow="Support"
        title="Support that stays"
        titleAccent="after handover."
        description="Service requests, AMC, remote and on-site support across Kerala — because infrastructure is only as good as the team standing behind it."
      />
      <section className="py-16 sm:py-24 border-b border-white/10">
        <div className="nx-wrap">
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {ACTIONS.map((a, i) => {
              const Icon = a.icon;
              const inner = (
                <div className="group h-full bg-nx-navy hover:bg-nx-navy2 p-7 transition-colors" data-testid={`support-action-${i}`}>
                  <Icon size={24} className="text-nx-goldlight mb-5" strokeWidth={1.5} />
                  <h3 className="font-marcellus text-lg text-white mb-3">{a.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed mb-6">{a.text}</p>
                  <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-nx-goldlight">{a.cta} <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" /></span>
                </div>
              );
              return (
                <StaggerItem key={a.title}>
                  {a.to.startsWith("#") ? <a href={a.to} className="block h-full">{inner}</a> : <Link to={a.to} className="block h-full">{inner}</Link>}
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-nx-navy2 border-b border-white/10" data-testid="support-faq">
        <div className="nx-wrap max-w-3xl">
          <Reveal>
            <h2 className="font-marcellus text-3xl sm:text-4xl text-white mb-10">Frequently asked questions</h2>
          </Reveal>
          <div className="border-t border-white/10">
            {FAQS.map((f, i) => <Faq key={f.q} q={f.q} a={f.a} i={i} />)}
          </div>
        </div>
      </section>

      <section id="support-form" className="py-16 sm:py-24">
        <div className="nx-wrap grid lg:grid-cols-[1.6fr_1fr] gap-14">
          <div>
            <Reveal>
              <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">Service Request</span>
              <h2 className="font-marcellus text-3xl sm:text-4xl text-white mt-4 mb-3">Tell us what you need.</h2>
              <p className="text-base text-slate-400 mb-10 max-w-lg leading-relaxed">Site, system, problem and priority — enough for our support team to diagnose and respond, remotely where possible, on-site where needed.</p>
            </Reveal>
            <Reveal delay={0.15}>
              <ServiceRequestForm />
            </Reveal>
          </div>
          <div className="lg:pt-20">
            <Reveal delay={0.2}>
              <div className="border border-white/10 bg-white/[0.03] p-8">
                <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400 mb-6">Direct support lines</p>
                <ContactAside tone="dark" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand title="Not a customer yet?" text="Start with a site assessment — we will document your infrastructure and show you exactly where you stand." />
      <SiteFooter />
    </div>
  );
}
